<template>
  <DefaultCrudView
    @create-new="newCampaign"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">
      <v-select
        v-model="selectedCustomerId"
        :items="customers.map(c => ({ text: c.customer_name || c.title || c.name, value: c.customer_id }))"
        item-text="text"
        item-value="value"
        :label="$t('customer')"
        prepend-icon="mdi-account-group"
        clearable
        class="mb-4"
      ></v-select>

      <v-data-table
        :headers="headers"
        :items="campaigns"
        class="elevation-1"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:item.customer_id="{ item }">
          {{ getCustomerTitle(item.customer_id) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click="editCampaign(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>

          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click="deleteCampaign(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t('delete') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>

      <DefaultModal :title="getModalTitle()" ref="modal" @submit="save">
        <template v-slot:content>
          <DynamicFormContent :fields="fields"></DynamicFormContent>
        </template>
      </DefaultModal>
    </template>
  </DefaultCrudView>
</template>

<style scoped>
  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }
</style>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'

import AppActions from '@/store/app/actions-types'
import AppMutations from '@/store/app/mutations-types'
import CustomerAPI from '@/services/api/business/customer'

import CampaignAPI from '@/services/api/job/campaign'

export default {
  name: 'Campaign',

  components: {
    DefaultCrudView,
    DefaultModal,
    DynamicFormContent
  },

  computed: {
    ...mapGetters('app', [
      'getIconForOperationSettingsItem',
      'getUserApplicationById'
    ]),

    breadcrumbs() {
      return [
        {
          text: this.$t('campaigns'),
          prependIcon: 'mdi-bullhorn',
        },
      ]
    },

    fields() {
      const baseFields = [
        {
          key: 'campaign_title',
          name: 'campaign_title',
          label: this.$t('campaign_title'),
          type: 'text',
          value: this.selectedCampaign ? (this.selectedCampaign.campaign_title || this.selectedCampaign.name || this.selectedCampaign.title) : '',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          autofocus: true,
          cols: 12
        },
      ]

      // Only show customer selector in the modal when editing an existing campaign
      if (this.selectedCampaign) {
        baseFields.push({
          key: 'customer_id',
          name: 'customer_id',
          label: this.$t('customer'),
          type: 'select',
          value: this.selectedCampaign.customer_id || null,
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          items: this.customers.map(customer => ({
            text: customer.customer_name || customer.title || customer.name,
            value: customer.customer_id
          })),
          itemText: 'text',
          itemValue: 'value',
          cols: 12
        })
      }

      return baseFields
    },

    appInstance() {
      return this.getUserApplicationById(this.$route.params.appId)
    },
    appConfig() {
      if (!this.appInstance || !this.appInstance.config) return {}
      return this.parseApplicationConfig(this.appInstance.config)
    },
    appTenantId() {
      return (
        this.appConfig.tenant_id ||
        this.appConfig.tenantId ||
        (this.appConfig.tenant && (this.appConfig.tenant.id || this.appConfig.tenant.tenant_id)) ||
        null
      )
    }
  },

  data() {
    return {
      loading: true,
      campaigns: [],
      options: {
        sortBy: ['campaign_title'],
        sortDesc: [false],
      },
      itemsLength: 0,
      crudActions: [
        {
          text: 'create_campaign',
          eventToEmit: 'create-new',
        }
      ],
      customers: [],
      selectedCustomerId: null,
      selectedCampaign: null,
      headers: [
        { text: this.$t('campaign_title'), value: 'campaign_title', sortable: true },
        { text: this.$t('customer'), value: 'customer_id', sortable: true },
        { text: this.$t('actions'), value: 'actions', sortable: false, width: '180px' }
      ]
    }
  },

  mounted() {
    this.selectedCampaign = null

    // Ensure the business app base URL is set in the store so AxiosBusiness
    // uses the correct endpoint (e.g. http://127.0.0.1:100) instead of falling
    // back to the frontend URL.
    const businessAppId = this.appConfig.business_app_inst_id || this.appConfig.business_app_id
    if (businessAppId) {
      this[AppMutations.SET_SELECTED_BUSINESS_APP](businessAppId)
    }

    this.loadCustomers()
  },

  watch: {
    options: {
      handler () {
        this.reloadData()
      },
      deep: true,
    },

    selectedCustomerId () {
      this.reloadData()
    },
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE
    ]),

    ...mapMutations('app', [
      AppMutations.SET_SELECTED_BUSINESS_APP
    ]),

    parseApplicationConfig(string) {
      try {
        return JSON.parse(string)
      } catch (e) {
        return {}
      }
    },

    newCampaign() {
      this.selectedCampaign = null
      this.$refs.modal.openModal()
    },

    save() {
      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      const formData = {}
      this.fields.forEach(field => {
        if (field.type === 'number') {
          formData[field.name] = field.value !== '' ? Number(field.value) : null
        } else {
          formData[field.name] = field.value
        }
      })

      let apiCall
      if (this.selectedCampaign) {
        const campaignId = this.selectedCampaign.campaign_id || this.selectedCampaign.id
        if (!campaignId) {
          console.error('Campaign ID not found in selected campaign object:', this.selectedCampaign)
          this.$refs.modal.errorMessage = 'Campaign ID not found'
          return
        }
        apiCall = CampaignAPI.updateCampaign(campaignId, formData)
      } else {
        // When creating, inject the customer selected from the main page dropdown
        formData.customer_id = this.selectedCustomerId
        apiCall = CampaignAPI.createCampaign(formData)
      }

      apiCall
        .then(() => {
          const message = this.selectedCampaign ? 'Campaign updated successfully' : 'Campaign created successfully'
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t(message))
          this.$refs.modal.closeModal()
          this.reloadData()
        })
        .catch((error) => {
          this.$refs.modal.submitting = false
          if (error.response && error.response.data) {
            this.$refs.modal.errorMessage = error.response.data.message || 'An error occurred'
          }
        })
    },

    reloadData() {
      let options = { ...this.$route.query }

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        options.direction = 'DESC'
      }

      options.limit = this.options.itemsPerPage
      options.offset = this.options.itemsPerPage * (this.options.page - 1)

      options['filter[active]'] = true

      this.loading = true

      const apiCall = this.selectedCustomerId
        ? CampaignAPI.getCampaignsByCustomer(this.selectedCustomerId, options)
        : CampaignAPI.getAllCampaigns(options)

      apiCall
        .then((result) => {
          const data = result.data
          // Handle both { items: [], total: N } and a plain array response
          const rawItems = Array.isArray(data) ? data : (data?.items || [])
          const total = Array.isArray(data) ? data.length : (data?.total || 0)
          this.campaigns = rawItems.filter(item => item.active !== false)
          this.itemsLength = total
          this.loading = false
        })
        .catch(() => this.loading = false)
    },

    editCampaign(campaign) {
      this.selectedCampaign = campaign
      this.$refs.modal.openModal()
    },

    deleteCampaign(campaign) {
      if (confirm(this.$t('confirm_delete_campaign'))) {
        const campaignId = campaign.campaign_id || campaign.id
        if (!campaignId) {
          console.error('Campaign ID not found in campaign object:', campaign)
          return
        }
        CampaignAPI.deleteCampaign(campaignId)
          .then(() => {
            this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('Campaign deleted successfully'))
            this.reloadData()
          })
          .catch((error) => {
            console.error('Error deleting campaign:', error)
          })
      }
    },

    getCustomerTitle(id) {
      const customer = this.customers.find(f => f.customer_id == id)
      return customer ? (customer.customer_name || customer.title || customer.name) : ''
    },

    getModalTitle() {
      return this.selectedCampaign ? this.$t('edit_campaign') : this.$t('create_campaign')
    },

    async loadCustomers() {
      const params = {
        order: 'customer_name',
        limit: 1000,
        offset: 0,
        'filter[active]': true,
      }

      try {
        const response = await CustomerAPI.getAllCustomers(params)
        this.customers = response?.data?.items || response?.data || []

        // Auto-select the first customer so the list loads filtered on startup
        if (this.customers.length > 0) {
          this.selectedCustomerId = this.customers[0].customer_id
        } else {
          // No customers — still load campaigns unfiltered
          this.reloadData()
        }
      } catch (error) {
        console.error('[Campaign] Error loading customers:', error)
        this.customers = []
        this.reloadData()
      }
    },
  }
}
</script>
